import { h, defineComponent, ref, watch } from 'vue';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { renderAnalyticsTeaserPage } from '@/ui/pages/teasers/AnalyticsTeaserPage';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import { CHART_TYPE } from '@/_legacy/constants';
import { encodeIssueQuery, IssueQuery } from '@/services/api/resources/analytics/query';
import { col, cols } from '@/ui/components';
import NumberCard from '@/_legacy/views/Analytics/components/NumberCard.vue';
import { t, toUnixTimestamp } from '@/ui/i18n/i18n';
import { pushRoute } from '@/ui/helpers/uiHelper';
import GroupedChartCard from '@/_legacy/views/Analytics/components/GroupedChartCard.vue';
import TimeToResolutionCard from '@/_legacy/views/Analytics/Issues/TimeToResolutionCard.vue';
import PieChartCard from '@/_legacy/views/Analytics/components/PieChartCard.vue';
import InfoCircleOutlineIcon from '@/_legacy/components/Icons/InfoCircleOutlineIcon.vue';
import { useTask } from '@/ui/concerns';
import { groupedIssueList } from '@/services/api/resources/analytics';
import { GroupByDates } from '@/services/api/resources/analytics/types';
import { IssueStatus } from '@/entities/todo';

interface IssuesPageChartsProps {
  query: IssueQuery;
  issueCount: number;
}

export const IssuesPageCharts = defineComponent<IssuesPageChartsProps>({
  name: 'IssuesPageCharts',
  props: ['query', 'issueCount'],
  setup(props) {
    if (!hasFeature('analytics')) {
      return renderAnalyticsTeaserPage;
    }

    if (!hasPermission(Permissions.VIEW_ALL_ANALYTICS)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);
    const chartType = ref(CHART_TYPE.LINE);
    const chartGroupBy = ref(GroupByDates.Week);
    const chartData = ref({});
    const statusChartData = ref({});
    const punctualityChartData = ref({});
    const averageTimeToResolve = ref<number>();

    const unsetSort = (query: IssueQuery) => {
      const queryNoSort = { ...query };
      queryNoSort.sort = '';
      return queryNoSort;
    };

    const getList = useTask(
      () => {
        initialLoading.value = true;
        return groupedIssueList(chartGroupBy.value, unsetSort(props.query));
      },
      response => {
        const dateFormatted = (start: Date | null) => start ? toUnixTimestamp(start.getTime()) : null;

        averageTimeToResolve.value = response.meta.averageTimeToResolve;

        chartData.value = {
          labels: response.data.map(result => dateFormatted(result.group.start)),
          series: [response.data.map((result, index) => ({
            x: index,
            y: result.issueCount,
            meta: {
              start: dateFormatted(result.group.start),
              end: dateFormatted(result.group.end),
            },
          }))],
          format: 'analyticsInteger',
        };

        statusChartData.value = {
          series: [
            {
              value: response.meta.openIssueCount,
              className: 'blue',
              label: t('status.pending'),
              meta: { status: IssueStatus.Pending },
            },
            {
              value: response.meta.inProgressIssueCount,
              className: 'orange',
              label: t('status.in-progress'),
              meta: { status: IssueStatus.InProgress },
            },
            {
              value: response.meta.resolvedIssueCount,
              className: 'green',
              label: t('status.done'),
              meta: { status: IssueStatus.Done },
            },
          ],
        };

        punctualityChartData.value = {
          series: [
            {
              value: response.meta.resolvedInTimeIssueCount,
              className: 'green',
              label: t('issues.in-time'),
              meta: { overdue: 0 },
            },
            {
              value: response.meta.overdueIssueCount,
              className: 'red',
              label: t('issues.too-late'),
              meta: { overdue: 1 },
            },
          ],
        };

        initialLoading.value = false;
      },
    );

    watch([
      () => props.query,
      chartGroupBy,
    ], getList.run, { deep: true });

    const redirectToExplain = (query: IssueQuery) => {
      pushRoute({
        name: 'analyticsIssuesExplain',
        query: encodeIssueQuery(query),
      });
    };

    function handleStatusCardClick(meta: { status?: IssueStatus; overdue?: boolean }) {
      redirectToExplain({
        ...unsetSort(props.query),
        status: meta.status !== undefined ? [meta.status] : [],
        overdue: meta.overdue,
      });
    }

    return function render() {
      return h('div', [
        cols([
          col([
            h(NumberCard, {
              props: {
                loading: initialLoading.value,
                color: 'var(--analytics-purple)',
                label: t('issues.count'),
                number: props.issueCount,
              },
              class: ['h-full'],
              on: {
                numberClick: () => redirectToExplain(unsetSort(props.query)),
              },
            }),
          ], true, { 'number-column': true }),
          col([
            //legacy, not compatible with Vue typings
            //@ts-ignore
            h(GroupedChartCard, {
              class: ['w-full'],
              props: {
                color: 'purple',
                loading: initialLoading.value,
                data: chartData.value,
                startsAt: toUnixTimestamp(props.query.start.getTime()),
                endsAt: toUnixTimestamp(props.query.end.getTime()),
                type: chartType.value,
                groupBy: chartGroupBy.value,
                tooltipText: t('common.issues'),
              },
              on: {
                typeChanged: (newType: CHART_TYPE) => {
                  chartType.value = newType;
                },
                groupByChanged: (newGroup: GroupByDates) => {
                  chartGroupBy.value = newGroup;
                },
              },
            }),
          ]),
        ]),
        cols([
          col([
            //legacy, not compatible with Vue typings
            //@ts-ignore
            h(PieChartCard, {
              props: {
                loading: initialLoading.value,
                chartData: statusChartData.value,
              },
              on: {
                rowClick: handleStatusCardClick,
                chartClick: handleStatusCardClick,
              },
            }, [t('issues.status')]),
          ], false, { 'is-two-fifths': true }),
          col([
            //legacy, not compatible with Vue typings
            //@ts-ignore
            h(PieChartCard, {
              props: {
                loading: initialLoading.value,
                chartData: punctualityChartData.value,
              },
              on: {
                rowClick: handleStatusCardClick,
                chartClick: handleStatusCardClick,
              },
            }, [
              h('div', { class: 'flex' }, [
                h('div', {}, [t('common.punctuality')]),
                h('div', {
                  class: 'self-start ml-1 -mt-1',
                  directives: [
                    {
                      name: 'tooltip',
                      value: t('issues.punctuality-chart-tooltip'),
                    },
                  ],
                }, [h(InfoCircleOutlineIcon)]),
              ]),
            ]),
          ], false, { 'is-two-fifths': true }),
          col([
            h(TimeToResolutionCard, {
              class: 'pl-4 average-time-card',
              props: {
                loading: initialLoading.value,
                data: averageTimeToResolve.value,
              },
              on: {
                numberClick: () => redirectToExplain(unsetSort(props.query)),
              },
            }, []),
          ], false, { 'is-one-fifths': true }),
        ], 'issues__tables'),
      ]);
    };
  },
});
